import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Address } from '../../../models/address-model';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { RealEstateOwned } from '../../../models/mortgage.model';
import { AddressStep } from '../../../models/wizard/address-step.model';
import { Constants } from '../../../services/constants';
import { UtilsService } from '../../../services/utils.service';
import { WizardFlowServiceBase } from '../../../services/wizard/wizard-flow-service.base';
import { AddressComponent } from '../../address/address.component';
import { RefiOptionsComponent } from '../refi-options/refi-options.component';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { reoMatchesAddress, normalizeAddressPart } from '../../reo-address-match.util';
import { isActive } from '../../../utils/entity-state';

@Component({
    selector: 'address-step',
    templateUrl: 'address-step.component.html',
    standalone: false
})
export class AddressStepComponent extends WizardStepComponentBase<AddressStep> implements OnInit {

  @ViewChild(AddressComponent) addressComponent: AddressComponent | undefined;

  @ViewChild(RefiOptionsComponent) refiOptionsComponent: RefiOptionsComponent | undefined;

  public address: Address = new Address();

  public readonly propertyAddressRefiPath = Constants.stepPaths.propertyAddressRefi;

  constructor(
    private readonly _injector: Injector,
    private readonly _wizardFlowService: WizardFlowServiceBase,
    private readonly _utilsService: UtilsService
  ) {
    super(_injector);
  }

  onNextClicked() {
    const addressValid = this.addressComponent?.validate() ?? true;
    const refiValid = this.refiOptionsComponent?.validate() ?? true;
    if (addressValid && refiValid) {
      this.step.setAddress(this.address);
      if (this.isRefinance) {
        this.ensureRefinanceAddressInReoList();
      }
      super.onNextClicked();
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.address = this.step.address;
  }

  public get mortgage(): MortgageApplication {
    return this._wizardFlowService.context.mortgage;
  }

  public get isRefinance(): boolean {
    return this.mortgage?.subjectProperty?.purposeOfLoan === 'Refinance';
  }

  private ensureRefinanceAddressInReoList(): void {
    const mortgage = this.mortgage;
    if (!mortgage.realEstateOwned) {
      mortgage.realEstateOwned = [];
    }

    // LLS-140: normalized match so a formatting difference doesn't create a duplicate
    // subject-property REO for the same physical property.
    const existingReo = mortgage.realEstateOwned.find(reo => isActive(reo) && reoMatchesAddress(reo, this.address));

    if (existingReo) {
      existingReo.isSubjectProperty = true;
    } else if (normalizeAddressPart(this.address.address1)) {
      // LLS-140: only create the subject-property REO once the street is identifiable, so a
      // blank/partial address never pushes a phantom REO on repeated passes through the step.
      const reo = new RealEstateOwned();
      reo.reoId = this._utilsService.generateUniqueNumericId();
      reo.address1 = this.address.address1 ?? '';
      reo.city = this.address.city ?? '';
      reo.state = this.address.state ?? '';
      reo.zipCode = this.address.zipCode ?? '';
      reo.isSubjectProperty = true;
      reo.dispositionStatus = 'Retained';
      reo.owningBorrowerIds = mortgage.borrowers.map(b => b.borrowerId).filter(id => id != null);
      mortgage.realEstateOwned.push(reo);
    }
  }
}
